import { useState } from 'react';
import './App.css';

type Language = 'en' | 'pt';
type TextPair = readonly [string, string];
type Category = readonly [string, readonly string[]];

type Copy = {
  nav: readonly string[];
  heroKicker: string;
  headline: string;
  subheadline: string;
  primaryCta: string;
  secondaryCta: string;
  ctaNote: string;
  stack: readonly string[];
  metrics: readonly TextPair[];
  aboutTitle: string;
  aboutLead: string;
  aboutBody: string;
  techTitle: string;
  techIntro: string;
  categories: readonly Category[];
  differentialsTitle: string;
  engineeringLead: string;
  differentials: readonly string[];
  contentTitle: string;
  contentIntro: string;
  contentLabel: string;
  posts: readonly TextPair[];
  faqTitle: string;
  faqIntro: string;
  faqs: readonly TextPair[];
  contactTitle: string;
  contactBody: string;
  contactCta: string;
};

const links = {
  github: 'https://github.com/GabrielPMagni/',
  linkedin: 'https://www.linkedin.com/in/gabriel-peres-magni/',
};

const content: Record<Language, Copy> = {
  en: {
    nav: ['About', 'Stack', 'Engineering', 'Content', 'FAQ', 'Contact'],
    heroKicker: 'Backend Engineering | Architecture | Automation',
    headline: 'Backend-focused Software Engineer building scalable APIs and automation systems.',
    subheadline:
      'I help technical teams design reliable services, integrate systems and automate operational workflows with maintainable TypeScript, Node.js, SQL and cloud-oriented architecture.',
    primaryCta: 'Start a LinkedIn conversation',
    secondaryCta: 'View GitHub',
    ctaNote: 'Best fit: backend, APIs, automation, architecture and platform work.',
    stack: ['Node.js', 'TypeScript', 'React', 'SQL', 'Docker', 'Cloud'],
    metrics: [
      ['Backend', 'APIs, services and integrations'],
      ['Architecture', 'Maintainable systems and clean boundaries'],
      ['Automation', 'Reliable workflows and operational speed'],
    ],
    aboutTitle: 'About',
    aboutLead: 'I build software for teams that value clarity, reliability and maintainability.',
    aboutBody:
      'My focus is backend engineering, APIs, automation and scalable applications. I care about fundamentals: HTTP, databases, security, networks, observability and performance. The outcome is practical: systems that are easier to evolve, operate and trust.',
    techTitle: 'Technologies',
    techIntro: 'A practical stack for building, integrating and operating modern systems.',
    categories: [
      ['Backend', ['Node.js', 'Express', 'REST APIs', 'Authentication', 'Service Architecture']],
      ['Frontend', ['React', 'TypeScript', 'Modern UI', 'Responsive Interfaces']],
      ['Database', ['SQL', 'PostgreSQL', 'Data Modeling', 'Query Design']],
      ['DevOps', ['Docker', 'CI/CD', 'Linux', 'Automation', 'GitHub']],
      ['Cloud', ['Cloud Architecture', 'Scalability', 'Infrastructure', 'Observability']],
      ['Tools', ['Git', 'VSCode', 'Postman', 'Monitoring']],
    ],
    differentialsTitle: 'Engineering Focus',
    engineeringLead: 'Architecture, reliability and operational clarity.',
    differentials: [
      'System architecture',
      'Process automation',
      'Performance',
      'Clean code',
      'Scalability',
      'Maintainability',
      'Security',
      'Networks and HTTP',
    ],
    contentTitle: 'Technical Content',
    contentIntro: 'Topics I study, document and apply in software projects.',
    contentLabel: 'Technical Note',
    posts: [
      ['Backend Architecture', 'Service boundaries, APIs, clean layers and maintainable modules.'],
      ['HTTP and Networks', 'Request lifecycle, protocols, latency, reliability and security.'],
      ['Automation and DevOps', 'Pipelines, scripts, containers and repeatable operational workflows.'],
    ],
    faqTitle: 'FAQ',
    faqIntro: 'Fast answers for recruiters, engineering leaders and technical peers.',
    faqs: [
      ['What does Gabriel specialize in?', 'Backend engineering, APIs, automation, software architecture and scalable systems.'],
      ['Which stack appears most often?', 'TypeScript, Node.js, React, SQL, Docker, CI/CD and cloud-oriented infrastructure.'],
      ['What is the best contact channel?', 'LinkedIn is the primary channel for technical conversations and professional opportunities.'],
    ],
    contactTitle: 'Contact',
    contactBody:
      'Have a backend, automation or architecture challenge worth discussing?',
    contactCta: 'Message on LinkedIn',
  },
  pt: {
    nav: ['Sobre', 'Stack', 'Engenharia', 'Conteúdo', 'FAQ', 'Contato'],
    heroKicker: 'Backend Engineering | Arquitetura | Automação',
    headline: 'Engenheiro de Software backend criando APIs escaláveis e automações.',
    subheadline:
      'Ajudo times técnicos a desenhar serviços confiáveis, integrar sistemas e automatizar fluxos operacionais com TypeScript, Node.js, SQL e arquitetura orientada à cloud.',
    primaryCta: 'Conversar no LinkedIn',
    secondaryCta: 'Ver GitHub',
    ctaNote: 'Melhor fit: backend, APIs, automação, arquitetura e plataformas.',
    stack: ['Node.js', 'TypeScript', 'React', 'SQL', 'Docker', 'Cloud'],
    metrics: [
      ['Backend', 'APIs, serviços e integrações'],
      ['Arquitetura', 'Sistemas manuteníveis e limites claros'],
      ['Automação', 'Fluxos confiáveis e velocidade operacional'],
    ],
    aboutTitle: 'Sobre',
    aboutLead: 'Construo software para times que valorizam clareza, confiabilidade e manutenção.',
    aboutBody:
      'Meu foco é engenharia backend, APIs, automação e aplicações escaláveis. Valorizo fundamentos: HTTP, bancos de dados, segurança, redes, observabilidade e performance. O resultado é prático: sistemas fáceis de evoluir, operar e confiar.',
    techTitle: 'Tecnologias',
    techIntro: 'Uma stack prática para construir, integrar e operar sistemas modernos.',
    categories: [
      ['Backend', ['Node.js', 'Express', 'APIs REST', 'Autenticação', 'Arquitetura de Serviços']],
      ['Frontend', ['React', 'TypeScript', 'UI Moderna', 'Interfaces Responsivas']],
      ['Banco de Dados', ['SQL', 'PostgreSQL', 'Modelagem de Dados', 'Design de Consultas']],
      ['DevOps', ['Docker', 'CI/CD', 'Linux', 'Automação', 'GitHub']],
      ['Cloud', ['Arquitetura Cloud', 'Escalabilidade', 'Infraestrutura', 'Observabilidade']],
      ['Ferramentas', ['Git', 'VSCode', 'Postman', 'Monitoramento']],
    ],
    differentialsTitle: 'Foco de Engenharia',
    engineeringLead: 'Arquitetura, confiabilidade e clareza operacional.',
    differentials: [
      'Arquitetura de sistemas',
      'Automação de processos',
      'Performance',
      'Clean code',
      'Escalabilidade',
      'Manutenibilidade',
      'Segurança',
      'Redes e HTTP',
    ],
    contentTitle: 'Conteúdo Técnico',
    contentIntro: 'Temas que estudo, documento e aplico em projetos de software.',
    contentLabel: 'Nota Técnica',
    posts: [
      ['Arquitetura Backend', 'Limites de serviços, APIs, camadas limpas e módulos manuteníveis.'],
      ['HTTP e Redes', 'Ciclo de requisição, protocolos, latência, confiabilidade e segurança.'],
      ['Automação e DevOps', 'Pipelines, scripts, containers e fluxos operacionais repetíveis.'],
    ],
    faqTitle: 'FAQ',
    faqIntro: 'Respostas rápidas para recrutadores, lideranças técnicas e pares de engenharia.',
    faqs: [
      ['Em que Gabriel é especializado?', 'Engenharia backend, APIs, automação, arquitetura de software e sistemas escaláveis.'],
      ['Quais tecnologias aparecem mais?', 'TypeScript, Node.js, React, SQL, Docker, CI/CD e infraestrutura orientada à cloud.'],
      ['Qual é o melhor canal de contato?', 'LinkedIn é o canal principal para conversas técnicas e oportunidades profissionais.'],
    ],
    contactTitle: 'Contato',
    contactBody:
      'Tem um desafio de backend, automação ou arquitetura para conversar?',
    contactCta: 'Enviar mensagem no LinkedIn',
  },
};

function App() {
  const [language, setLanguage] = useState<Language>('en');
  const copy = content[language];
  const oppositeLanguage: Language = language === 'en' ? 'pt' : 'en';

  return (
    <main className="site-shell">
      <div className="ambient-grid" aria-hidden="true" />

      <header className="site-header">
        <a className="brand" href="#top" aria-label="Gabriel Magni home">
          <span className="brand-mark">GM</span>
          <span>Gabriel Magni</span>
        </a>

        <nav className="desktop-nav" aria-label="Primary navigation">
          {copy.nav.map((item, index) => (
            <a key={item} href={`#${['about', 'stack', 'engineering', 'content', 'faq', 'contact'][index]}`}>
              {item}
            </a>
          ))}
        </nav>

        <button
          className="language-toggle"
          type="button"
          aria-label={`Switch language to ${oppositeLanguage.toUpperCase()}`}
          onClick={() => setLanguage(oppositeLanguage)}
        >
          {language.toUpperCase()}
          <span>{oppositeLanguage.toUpperCase()}</span>
        </button>
      </header>

      <section className="hero-section" id="top">
        <div className="hero-copy">
          <p className="eyebrow">{copy.heroKicker}</p>
          <h1>{copy.headline}</h1>
          <p className="hero-subheadline">{copy.subheadline}</p>

          <div className="hero-actions" aria-label="Primary links">
            <a className="button button-primary" href={links.linkedin} target="_blank" rel="noreferrer">
              {copy.primaryCta}
            </a>
            <a className="button button-secondary" href={links.github} target="_blank" rel="noreferrer">
              {copy.secondaryCta}
            </a>
          </div>
          <p className="cta-note">{copy.ctaNote}</p>

          <div className="stack-strip" aria-label="Main stack">
            {copy.stack.map((item) => (
              <span key={item}>{item}</span>
            ))}
          </div>
        </div>

        <aside className="system-panel" aria-label="Engineering indicators">
          <div className="panel-header">
            <span className="status-dot" />
            <span>system.profile</span>
          </div>
          {copy.metrics.map(([title, description]) => (
            <div className="metric-row" key={title}>
              <strong>{title}</strong>
              <span>{description}</span>
            </div>
          ))}
        </aside>
      </section>

      <section className="content-band about-band" id="about">
        <div className="section-heading">
          <p className="eyebrow">{copy.aboutTitle}</p>
          <h2>{copy.aboutLead}</h2>
        </div>
        <p className="about-text">{copy.aboutBody}</p>
      </section>

      <section className="content-band" id="stack">
        <div className="section-heading">
          <p className="eyebrow">{copy.techTitle}</p>
          <h2>{copy.techIntro}</h2>
        </div>
        <div className="tech-grid">
          {copy.categories.map(([category, items]) => (
            <article className="tech-card" key={category}>
              <h3>{category}</h3>
              <div>
                {items.map((item) => (
                  <span key={item}>{item}</span>
                ))}
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="content-band" id="engineering">
        <div className="section-heading">
          <p className="eyebrow">{copy.differentialsTitle}</p>
          <h2>{copy.engineeringLead}</h2>
        </div>
        <div className="differentials-grid">
          {copy.differentials.map((item, index) => (
            <article className="differential-card" key={item}>
              <span>{String(index + 1).padStart(2, '0')}</span>
              <h3>{item}</h3>
            </article>
          ))}
        </div>
      </section>

      <section className="content-band" id="content">
        <div className="section-heading">
          <p className="eyebrow">{copy.contentTitle}</p>
          <h2>{copy.contentIntro}</h2>
        </div>
        <div className="posts-grid">
          {copy.posts.map(([title, description]) => (
            <article className="post-card" key={title}>
              <span>{copy.contentLabel}</span>
              <h3>{title}</h3>
              <p>{description}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="content-band" id="faq">
        <div className="section-heading">
          <p className="eyebrow">{copy.faqTitle}</p>
          <h2>{copy.faqIntro}</h2>
        </div>
        <div className="faq-grid">
          {copy.faqs.map(([question, answer]) => (
            <article className="faq-card" key={question}>
              <h3>{question}</h3>
              <p>{answer}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="contact-section" id="contact">
        <div>
          <p className="eyebrow">{copy.contactTitle}</p>
          <h2>{copy.contactBody}</h2>
        </div>
        <a className="button button-primary" href={links.linkedin} target="_blank" rel="noreferrer">
          {copy.contactCta}
        </a>
      </section>
    </main>
  );
}

export default App;
